"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { AuditTrailSnapshot } from "./audit-trail-snapshot";
import { useGroupedLogs } from "./use-grouped-logs";
import { carePlanQueryOptions } from "@/api/provider/patient/care-plan-query";
interface AuditTrailDialogProps extends React.ComponentPropsWithoutRef<typeof Dialog> {
  carePlanId: string;
  patientId: string;
  children?: React.ReactNode;
}

/**
 * Full audit trail dialog with split-panel layout.
 *
 * Left panel: timeline of revisions (clickable)
 * Right panel: care plan snapshot at the selected revision with diff decorations
 *
 * State is lifted here and passed down — the timeline and snapshot are
 * pure rendering components that compose existing CarePlan primitives.
 */
export function AuditTrailDialog(t0) {
  const $ = _c(30);
  let carePlanId;
  let children;
  let patientId;
  let props;
  if ($[0] !== t0) {
    ({
      carePlanId,
      patientId,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = carePlanId;
    $[2] = children;
    $[3] = patientId;
    $[4] = props;
  } else {
    carePlanId = $[1];
    children = $[2];
    patientId = $[3];
    props = $[4];
  }
  const [open, setOpen] = useState(false);
  let t1;
  if ($[5] !== carePlanId) {
    t1 = carePlanQueryOptions.logs(carePlanId);
    $[5] = carePlanId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = open || !!props.open;
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: t4,
    isLoading
  } = useQuery(t3);
  let t5;
  if ($[10] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const logs = t5;
  let t6;
  if ($[12] !== logs) {
    t6 = logs ?? [];
    $[12] = logs;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const groups = useGroupedLogs(t6);
  let t7;
  if ($[14] !== children) {
    t7 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[14] = children;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader><DialogTitle>Care Plan Edit History</DialogTitle></DialogHeader>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== carePlanId || $[18] !== groups || $[19] !== isLoading || $[20] !== patientId) {
    t9 = <DialogBody className="max-h-[70vh]">{isLoading ? <LoadingSkeleton /> : groups.length === 0 ? <p className="py-8 text-center text-body text-foreground-secondary">No edit history found.</p> : <div className="flex gap-6 overflow-hidden h-full"><AuditTrailSnapshot carePlanId={carePlanId} patientId={patientId} /></div>}</DialogBody>;
    $[17] = carePlanId;
    $[18] = groups;
    $[19] = isLoading;
    $[20] = patientId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogFooter><DialogClose asChild={true}><Button variant="primary">Close</Button></DialogClose></DialogFooter>;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t9) {
    t11 = <DialogContent className="max-w-7xl">{t8}{t9}{t10}</DialogContent>;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== open || $[26] !== props || $[27] !== t11 || $[28] !== t7) {
    t12 = <Dialog open={open} onOpenChange={setOpen} {...props}>{t7}{t11}</Dialog>;
    $[25] = open;
    $[26] = props;
    $[27] = t11;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function LoadingSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-64 shrink-0 space-y-3 border-r border-border pr-4">{Array.from({
        length: 4
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex gap-6">{t0}<div className="flex-1 space-y-4"><Skeleton className="h-20 w-full rounded-lg" /><Skeleton className="h-16 w-48" /><Skeleton className="h-16 w-48" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <div key={i} className="space-y-1.5 px-3 py-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-20" /></div>;
}